import React, { useCallback, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import type { DoctorShiftRule } from '../../../../domain/doctorShiftRule';
import { formatTimeCondition, RULE_TYPE_LABELS } from '../../../../domain/doctorShiftRule';

interface DoctorShiftRuleDeleteDialogProps {
  open: boolean;
  rule: DoctorShiftRule | null;
  onClose: () => void;
  onConfirm: (id: string) => Promise<boolean>;
  isLoading: boolean;
}

/**
 * 医師シフトルール削除確認ダイアログ
 *
 * - 確認メッセージ（医師名含む）
 * - 削除・キャンセルボタン
 * - ローディング状態
 */
const DoctorShiftRuleDeleteDialog: React.FC<DoctorShiftRuleDeleteDialogProps> = memo(({
  open,
  rule,
  onClose,
  onConfirm,
  isLoading
}) => {
  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  const handleConfirm = useCallback(async () => {
    if (!rule) return;
    await onConfirm(rule.id);
  }, [rule, onConfirm]);

  if (!rule) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          <Typography variant="h6">
            シフトルールを削除
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          以下のシフトルールを削除してもよろしいですか？
        </Typography>

        <Box
          sx={{
            p: 2,
            bgcolor: 'grey.100',
            borderRadius: 1
          }}
        >
          <Typography variant="subtitle2" fontWeight={500}>
            {rule.doctorFullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            医師コード: {rule.doctorCode}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            ルールタイプ: {RULE_TYPE_LABELS[rule.ruleType]}
          </Typography>
          {rule.timeConditions.map((tc, index) => (
            <Typography key={index} variant="body2" color="text.secondary">
              {formatTimeCondition(tc)}
            </Typography>
          ))}
        </Box>

        <Typography
          variant="body2"
          color="error"
          sx={{ mt: 2 }}
        >
          ※ この操作は取り消せません
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isLoading}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? '削除中...' : '削除'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

DoctorShiftRuleDeleteDialog.displayName = 'DoctorShiftRuleDeleteDialog';

export default DoctorShiftRuleDeleteDialog;
